import { useState, useCallback, useMemo } from "react";
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isBefore,
  isWithinInterval,
} from "date-fns";

interface UseCalendarDateLogicProps {
  unavailableDates: Date[];
  isMobile: boolean;
}

export const useCalendarDateLogic = ({
  unavailableDates,
  isMobile,
}: UseCalendarDateLogicProps) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const memoizedCalendarData = useMemo(() => {
    const currentMonth = startOfMonth(currentDate);
    const nextMonth = startOfMonth(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
    );

    const generateCalendarDays = (monthDate: Date) => {
      const monthStart = startOfMonth(monthDate);
      const monthEnd = endOfMonth(monthDate);

      const startDate = new Date(monthStart);
      startDate.setDate(startDate.getDate() - monthStart.getDay());

      const endDate = new Date(monthEnd);
      endDate.setDate(endDate.getDate() + (6 - monthEnd.getDay()));

      return eachDayOfInterval({ start: startDate, end: endDate });
    };

    return {
      currentMonth,
      nextMonth,
      currentMonthDays: generateCalendarDays(currentMonth),
      nextMonthDays: isMobile ? [] : generateCalendarDays(nextMonth),
    };
  }, [currentDate, isMobile]);

  const unavailableDatesSet = useMemo(() => {
    return new Set(unavailableDates.map((date) => date.getTime()));
  }, [unavailableDates]);

  const isDateBooked = useCallback(
    (date: Date) => unavailableDatesSet.has(date.getTime()),
    [unavailableDatesSet],
  );

  const isDatePast = useCallback(
    (date: Date) => isBefore(date, new Date()),
    [],
  );

  const isDateUnavailable = useCallback(
    (date: Date) => isDateBooked(date) || isDatePast(date),
    [isDateBooked, isDatePast],
  );

  const isDateSelected = useCallback(
    (date: Date, checkInDate?: Date, checkOutDate?: Date) => {
      if (checkInDate && isSameDay(date, checkInDate)) return "start";
      if (checkOutDate && isSameDay(date, checkOutDate)) return "end";
      if (
        checkInDate &&
        checkOutDate &&
        isWithinInterval(date, { start: checkInDate, end: checkOutDate })
      )
        return "range";
      return false;
    },
    [],
  );

  return {
    currentDate,
    setCurrentDate,
    memoizedCalendarData,
    isDateUnavailable,
    isDateBooked,
    isDatePast,
    isDateSelected,
  };
};
